<template>
  <div class="modal-overlay content-picker-overlay" @click.self="$emit('close')">
    <div class="modal-dialog content-picker-dialog" @click.stop>
      <div class="modal-content">
        <div class="modal-header">
          <h3><i class="bi bi-collection"></i> Add training content</h3>
          <button @click="$emit('close')" class="close-button">&times;</button>
        </div>

        <div class="modal-body">
          <div class="picker-tabs">
            <button type="button" :class="['picker-tab', { active: activeTab === 'programmes' }]"
              @click="activeTab = 'programmes'">
              <i class="bi bi-collection"></i> Programmes
              <span v-if="selectedProgrammes.length > 0" class="picker-count">{{ selectedProgrammes.length }}</span>
            </button>
            <button type="button" :class="['picker-tab', { active: activeTab === 'drills' }]"
              @click="activeTab = 'drills'">
              <i class="bi bi-clipboard-check"></i> Drills
              <span v-if="selectedDrills.length > 0" class="picker-count">{{ selectedDrills.length }}</span>
            </button>
          </div>

          <div class="picker-search">
            <i class="bi bi-search"></i>
            <input type="text" v-model="searchQuery" :placeholder="activeTab === 'programmes'
              ? 'Search programmes...'
              : 'Search drills...'
              " />
          </div>

          <div v-if="loading" class="picker-empty">Loading library...</div>

          <template v-else-if="activeTab === 'programmes'">
            <div v-if="filteredProgrammes.length === 0" class="picker-empty">
              No programmes found.
            </div>
            <ul v-else class="picker-list">
              <li v-for="programme in filteredProgrammes" :key="programme.id"
                :class="['picker-row', { selected: isProgrammeSelected(programme.id) }]"
                @click="toggleProgramme(programme)">
                <i :class="isProgrammeSelected(programme.id)
                  ? 'bi bi-check-circle-fill'
                  : 'bi bi-circle'
                  "></i>
                <div class="picker-row-text">
                  <span class="picker-row-title">{{ programme.title }}</span>
                  <span class="picker-row-meta">
                    <span v-if="programme.category">{{ programme.category }} · </span>
                    {{ programme.drill_count || 0 }} drills
                    <span v-if="programme.session_time"> · {{ programme.session_time }}</span>
                  </span>
                </div>
              </li>
            </ul>
          </template>

          <template v-else>
            <div v-if="filteredDrills.length === 0" class="picker-empty">
              No drills found.
            </div>
            <ul v-else class="picker-list">
              <li v-for="drill in filteredDrills" :key="drill.id"
                :class="['picker-row', { selected: isDrillSelected(drill.id) }]" @click="toggleDrill(drill)">
                <i :class="isDrillSelected(drill.id)
                  ? 'bi bi-check-circle-fill'
                  : 'bi bi-circle'
                  "></i>
                <div class="picker-row-text">
                  <span class="picker-row-title">{{ drill.name }}</span>
                  <span class="picker-row-meta">
                    <span v-if="drill.category">{{ drill.category }}</span>
                    <span v-if="drill.training_type"> · {{ drill.training_type }}</span>
                  </span>
                </div>
              </li>
            </ul>
          </template>
        </div>

        <div class="modal-footer picker-footer">
          <span class="picker-selection-summary">
            {{ selectedProgrammes.length }} programme{{ selectedProgrammes.length === 1 ? '' : 's' }},
            {{ selectedDrills.length }} drill{{ selectedDrills.length === 1 ? '' : 's' }} selected
          </span>
          <button type="button" class="green-button" @click="confirmSelection">Attach</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { TrainingPlan } from "@/services/training-plan-service.js";
import { TrainingProgramme } from "@/services/training-programme-service.js";
import { TRAINING_SECTIONS } from "@/constants/training-sections.js";

export default {
  name: "TrainingContentPicker",
  props: {
    clubId: {
      type: [String, Number],
      required: true,
    },
    section: {
      type: String,
      default: TRAINING_SECTIONS.TRAINING,
    },
    preselectedDrills: {
      type: Array,
      default: () => [],
    },
    preselectedProgrammes: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["close", "confirm"],
  data() {
    return {
      loading: false,
      activeTab: "programmes",
      searchQuery: "",
      programmes: [],
      drills: [],
      selectedProgrammes: [...this.preselectedProgrammes],
      selectedDrills: [...this.preselectedDrills],
    };
  },
  computed: {
    filteredProgrammes() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return this.programmes;
      return this.programmes.filter(
        (p) =>
          (p.title && p.title.toLowerCase().includes(q)) ||
          (p.description && p.description.toLowerCase().includes(q))
      );
    },
    filteredDrills() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return this.drills;
      return this.drills.filter(
        (d) =>
          (d.name && d.name.toLowerCase().includes(q)) ||
          (d.introText && d.introText.toLowerCase().includes(q))
      );
    },
  },
  created() {
    this.loadLibrary();
  },
  methods: {
    async loadLibrary() {
      this.loading = true;
      try {
        const [progRes, drillRes] = await Promise.all([
          TrainingProgramme.findByClub(this.clubId, this.section),
          TrainingPlan.find({
            action: "gettrainingPlanByClubId",
            clubId: this.clubId,
            trainingType: this.section,
          }),
        ]);
        this.programmes = Array.isArray(progRes.data) ? progRes.data : [];
        this.drills = Array.isArray(drillRes.data) ? drillRes.data : [];
      } catch (err) {
        console.error("Error loading training library:", err);
      } finally {
        this.loading = false;
      }
    },
    isProgrammeSelected(id) {
      return this.selectedProgrammes.some((p) => p.id === id);
    },
    isDrillSelected(id) {
      return this.selectedDrills.some((d) => d.id === id);
    },
    toggleProgramme(programme) {
      if (this.isProgrammeSelected(programme.id)) {
        this.selectedProgrammes = this.selectedProgrammes.filter(
          (p) => p.id !== programme.id
        );
      } else {
        this.selectedProgrammes = [
          ...this.selectedProgrammes,
          { id: programme.id, title: programme.title },
        ];
      }
    },
    toggleDrill(drill) {
      if (this.isDrillSelected(drill.id)) {
        this.selectedDrills = this.selectedDrills.filter(
          (d) => d.id !== drill.id
        );
      } else {
        this.selectedDrills = [
          ...this.selectedDrills,
          { id: drill.id, name: drill.name },
        ];
      }
    },
    confirmSelection() {
      this.$emit("confirm", {
        programmes: this.selectedProgrammes,
        drills: this.selectedDrills,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.content-picker-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2500;
  padding: 1rem;
}

.content-picker-dialog {
  width: 100%;
  max-width: 620px;
  margin: 0;
}

.modal-content {
  background: #fff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  max-height: 85vh;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;

  h3 {
    margin: 0;
    font-size: 1.05rem;
  }

  .close-button {
    background: none;
    border: none;
    font-size: 1.5rem;
    line-height: 1;
    cursor: pointer;
    color: #64748b;
  }
}

.modal-body {
  padding: 1rem 1.25rem;
  overflow-y: auto;
  flex: 1;
}

.picker-tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.picker-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #374151;
  font-size: 0.88rem;
  font-weight: 500;
  cursor: pointer;

  &.active {
    background: #eff6ff;
    border-color: #3b82f6;
    color: #1d4ed8;
  }
}

.picker-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: #3b82f6;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 600;
}

.picker-search {
  position: relative;
  margin-bottom: 0.75rem;

  i {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
  }

  input {
    width: 100%;
    height: 36px;
    padding: 0 12px 0 32px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.88rem;
  }
}

.picker-list {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.picker-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f8fafc;
  }

  &.selected {
    background: #eff6ff;

    >i {
      color: #16a34a;
    }
  }

  >i {
    font-size: 1.1rem;
    color: #cbd5e1;
    flex-shrink: 0;
  }
}

.picker-row-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.picker-row-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1e293b;
}

.picker-row-meta {
  font-size: 0.78rem;
  color: #64748b;
}

.picker-empty {
  text-align: center;
  color: #64748b;
  font-size: 0.88rem;
  padding: 1.5rem 0;
}

.picker-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.9rem 1.25rem;
  border-top: 1px solid #e2e8f0;
}

.picker-selection-summary {
  font-size: 0.83rem;
  color: #475569;
}
</style>
